import { useTranslation } from 'react-i18next'
import OnboardingLayout from '../../components/OnboardingLayout'
import type { ProtocolGenerationPhase } from '../../lib/generateInitialProtocol'
import { ONBOARDING } from '../../lib/onboarding/formStyles'
import { ONBOARDING_STEP_COUNT } from '../../lib/onboarding'
import { useProtocolGenerationMotion } from '../../lib/protocolGenerationState'
import { normalizeTimeHHMM } from '../../lib/trainingSchedule'

interface Step8Props {
  onLaunch: () => void
  onPrevious: () => void
  allData: Record<string, unknown>
  isSubmitting?: boolean
  launchError?: string
  generationPhase?: ProtocolGenerationPhase | null
  stepIndex: number
}

const PHASE_ORDER: ProtocolGenerationPhase[] = [
  'metabolics_calculated',
  'training_sequenced',
  'macros_tiered',
  'meals_sequenced',
  'meal_images_ready',
  'budget_matched',
]

const Step8 = ({
  onLaunch,
  onPrevious,
  allData,
  isSubmitting = false,
  launchError = '',
  generationPhase = null,
  stepIndex,
}: Step8Props) => {
  const { t } = useTranslation()
  const isStreaming = isSubmitting && !launchError
  const { microLogText, progressPercent } = useProtocolGenerationMotion(isStreaming, generationPhase ?? null)

  const safeString = (value: unknown): string => {
    if (typeof value === 'string') return value
    if (typeof value === 'number') return String(value)
    return ''
  }

  const activeIndex = generationPhase
    ? PHASE_ORDER.indexOf(generationPhase)
    : isSubmitting
      ? 0
      : -1

  const currencySymbol = safeString(allData.currency_symbol) || '$'

  return (
    <OnboardingLayout
      step={stepIndex}
      totalSteps={ONBOARDING_STEP_COUNT}
      onNext={onLaunch}
      onPrev={onPrevious}
      isLastStep={true}
      isSubmitting={isSubmitting}
      headerProgress={isSubmitting ? progressPercent : undefined}
    >
      <div className="space-y-8">
        {isSubmitting ? (
          <div className="space-y-4">
            <div>
              <h2 className={ONBOARDING.heading}>{t('onboarding.generation.heading')}</h2>
              <p className={ONBOARDING.subtitle}>{t('onboarding.generation.subtitle')}</p>
            </div>

            <div className="space-y-3">
              {PHASE_ORDER.map((phase, index) => {
                const isComplete = activeIndex > index
                const isActive = activeIndex === index
                const isPending = activeIndex < index

                return (
                  <div
                    key={phase}
                    className={`relative overflow-hidden rounded-2xl border p-4 transition-all duration-500 ${
                      isActive && isStreaming
                        ? 'protocol-card-shimmer protocol-card-active-stream border-kilo-accent/50 bg-kilo-accent/5 ring-1 ring-kilo-accent/30'
                        : isActive
                          ? 'border-kilo-accent/40 bg-kilo-accent/5'
                          : isComplete
                            ? ONBOARDING.phaseComplete
                            : ONBOARDING.phasePending
                    }`}
                  >
                    <div className="relative z-10 flex items-start gap-3">
                      <div
                        className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                          isComplete
                            ? 'bg-kilo-accent text-black'
                            : isActive
                              ? 'border border-kilo-accent text-kilo-accent'
                              : `border border-kilo-line ${ONBOARDING.mutedSoft}`
                        }`}
                      >
                        {isComplete ? '✓' : isActive ? '•' : index + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={`font-semibold ${isPending ? ONBOARDING.muted : ONBOARDING.reviewValue}`}>
                          {t(`onboarding.generation.phases.${phase}.headline`)}
                        </p>
                        <p className={`mt-1 text-sm ${isPending ? ONBOARDING.mutedSoft : ONBOARDING.muted}`}>
                          {t(`onboarding.generation.phases.${phase}.body`)}
                        </p>
                        {isActive && isStreaming && microLogText ? (
                          <div className="mt-3 animate-pulse font-mono text-xs text-kilo-accent/70">{microLogText}</div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ) : (
          <>
            <div>
              <h2 className={ONBOARDING.heading}>{t('onboarding.review.heading')}</h2>
              <p className={ONBOARDING.subtitle}>{t('onboarding.review.subtitle')}</p>
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              <div className={ONBOARDING.reviewCard}>
                <h3 className={ONBOARDING.reviewHeading}>{t('onboarding.review.profile')}</h3>
                <ul className={`mt-3 space-y-1 text-sm ${ONBOARDING.muted}`}>
                  <li>
                    <span className={ONBOARDING.reviewValue}>{safeString(allData.first_name)}</span>{' '}
                    <span className={ONBOARDING.reviewValue}>{safeString(allData.last_name)}</span>
                  </li>
                  <li>
                    {t('onboarding.review.age')}: <span className={ONBOARDING.reviewValue}>{safeString(allData.dob)}</span>
                  </li>
                  <li>
                    {t('onboarding.review.height')}: <span className={ONBOARDING.reviewValue}>{safeString(allData.height_cm)}cm</span>
                  </li>
                  <li>
                    {t('onboarding.review.weight')}: <span className={ONBOARDING.reviewValue}>{safeString(allData.current_weight_kg)}kg</span>
                  </li>
                </ul>
              </div>

              <div className={ONBOARDING.reviewCard}>
                <h3 className={ONBOARDING.reviewHeading}>{t('onboarding.review.training')}</h3>
                <ul className={`mt-3 space-y-1 text-sm ${ONBOARDING.muted}`}>
                  <li>
                    {t('onboarding.review.goal')}:{' '}
                    <span className={`${ONBOARDING.reviewValue} capitalize`}>{safeString(allData.training_goal).replace(/_/g, ' ')}</span>
                  </li>
                  <li>
                    {t('onboarding.review.environment')}: <span className={`${ONBOARDING.reviewValue} capitalize`}>{safeString(allData.environment)}</span>
                  </li>
                  <li>
                    {t('onboarding.review.experience')}:{' '}
                    <span className={`${ONBOARDING.reviewValue} capitalize`}>{safeString(allData.experience_level)}</span>
                  </li>
                  <li>
                    {t('onboarding.review.split')}:{' '}
                    <span className={`${ONBOARDING.reviewValue} capitalize`}>{safeString(allData.training_split).replace(/_/g, ' ')}</span>
                  </li>
                </ul>
              </div>

              <div className={ONBOARDING.reviewCard}>
                <h3 className={ONBOARDING.reviewHeading}>{t('onboarding.review.logistics')}</h3>
                <ul className={`mt-3 space-y-1 text-sm ${ONBOARDING.muted}`}>
                  <li>
                    {t('onboarding.review.trainingDays')}:{' '}
                    <span className={ONBOARDING.reviewValue}>{safeString(allData.weekly_commitment)}x/week</span>
                  </li>
                  <li>
                    {t('onboarding.review.mealPrep')}:{' '}
                    <span className={`${ONBOARDING.reviewValue} capitalize`}>{safeString(allData.meal_prep_experience)}</span>
                  </li>
                  <li>
                    {t('onboarding.review.workoutTime')}:{' '}
                    <span className={ONBOARDING.reviewValue}>
                      {allData.preferred_workout_start
                        ? new Date(`1970-01-01T${normalizeTimeHHMM(String(allData.preferred_workout_start))}:00`).toLocaleTimeString(undefined, {
                            hour: 'numeric',
                            minute: '2-digit',
                          })
                        : '—'}
                    </span>
                  </li>
                  <li>
                    {t('onboarding.review.mealFrequency')}:{' '}
                    <span className={`${ONBOARDING.reviewValue} capitalize`}>{safeString(allData.meal_frequency).replace(/_/g, ' ')}</span>
                  </li>
                </ul>
              </div>

              <div className={ONBOARDING.reviewCard}>
                <h3 className={ONBOARDING.reviewHeading}>{t('onboarding.review.nutrition')}</h3>
                <ul className={`mt-3 space-y-1 text-sm ${ONBOARDING.muted}`}>
                  <li>
                    {t('onboarding.review.bodyFat')}: <span className={ONBOARDING.reviewValue}>{safeString(allData.body_fat_archetype)}%</span>
                  </li>
                  <li>
                    {t('onboarding.review.budget')}:{' '}
                    <span className={ONBOARDING.reviewValue}>
                      {currencySymbol}
                      {safeString(allData.weekly_budget_value)}/week
                    </span>
                  </li>
                  <li>
                    <span className={ONBOARDING.reviewValue}>
                      {allData.vegetarian ? t('onboarding.review.dietaryVegetarian') : t('onboarding.review.dietaryOmnivore')}
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <div className={ONBOARDING.reviewCard}>
              <h3 className={ONBOARDING.reviewHeading}>{t('onboarding.review.whatHappensNext')}</h3>
              <p className={`mt-2 text-sm ${ONBOARDING.muted}`}>{t('onboarding.review.whatHappensNextBody')}</p>
            </div>

            <button
              type="button"
              onClick={() => onPrevious()}
              className={`w-full rounded-2xl border border-kilo-border bg-kilo-nested px-5 py-2 text-sm font-semibold ${ONBOARDING.reviewValue} transition hover:border-kilo-accent/40`}
            >
              {t('onboarding.review.editPreviousSteps')}
            </button>
          </>
        )}

        {launchError ? <div className={ONBOARDING.error}>{launchError}</div> : null}
      </div>
    </OnboardingLayout>
  )
}

export default Step8
